export interface ListinoPricing {
  prezzo: number
  prezzo_scontato: number
  sconto_percentuale: string
  prezzo_netto: string
  priceInputMode: 'discount' | 'net'
}

export function parseNetPrice(input: string): number | null {
  const value = input.trim()
  if (!/^(?:\d+(?:[.,]\d{0,2})?|[.,]\d{1,2})$/.test(value)) return null
  const amount = Number(value.replace(',', '.'))
  return Number.isFinite(amount) ? amount : null
}

function formatDiscount(percentage: number): string {
  return Number(percentage.toFixed(3)).toString().replace('.', ',')
}

/** Keep the input text intact while accepting either decimal separator. */
export function parseDiscountPercentage(input: string): number | null {
  const value = input.trim()
  if (!value) return 0
  if (!/^(?:\d+(?:[.,]\d*)?|[.,]\d+)$/.test(value)) return null
  const percentage = Number(value.replace(',', '.'))
  return Number.isFinite(percentage) && percentage >= 0 && percentage <= 100
    ? percentage
    : null
}

/** The stored net price remains authoritative when opening an existing item. */
export function listinoPricingFromPrices(prezzo: number, prezzoScontato?: number): ListinoPricing {
  const percentage = prezzo > 0 && prezzoScontato !== undefined && prezzoScontato > 0
    ? Math.max(0, ((prezzo - prezzoScontato) / prezzo) * 100)
    : 0
  return {
    prezzo,
    prezzo_scontato: prezzoScontato ?? 0,
    sconto_percentuale: percentage === 0 ? '' : formatDiscount(percentage),
    prezzo_netto: prezzo > 0 ? String(prezzoScontato || prezzo).replace('.', ',') : '',
    priceInputMode: prezzoScontato && prezzoScontato > 0 ? 'net' : 'discount',
  }
}

/** Keep the last edited net amount or percentage when the base price changes. */
export function updateListinoPricing<T extends ListinoPricing>(
  current: T,
  changes: Partial<Pick<ListinoPricing, 'prezzo' | 'sconto_percentuale' | 'prezzo_netto'>>,
): T {
  const next = { ...current, ...changes }
  if (changes.prezzo_netto !== undefined) next.priceInputMode = 'net'
  else if (changes.sconto_percentuale !== undefined) next.priceInputMode = 'discount'

  if (next.priceInputMode === 'net') {
    const netPrice = parseNetPrice(next.prezzo_netto)
    if (netPrice !== null && next.prezzo > 0) {
      // The typed net amount is authoritative; the displayed percentage is rounded.
      next.prezzo_scontato = netPrice === next.prezzo ? 0 : netPrice
      next.sconto_percentuale = formatDiscount(Math.max(0, (next.prezzo - netPrice) / next.prezzo * 100))
    }
    return next
  }

  const percentage = parseDiscountPercentage(next.sconto_percentuale)
  if (percentage !== null) {
    const discountedPrice = percentage > 0 && next.prezzo > 0
      ? Math.round((next.prezzo - next.prezzo * percentage / 100) * 100) / 100
      : 0
    next.prezzo_scontato = discountedPrice === next.prezzo ? 0 : discountedPrice
    const netPrice = percentage > 0 ? discountedPrice : next.prezzo
    next.prezzo_netto = next.prezzo > 0 ? String(netPrice).replace('.', ',') : ''
  }
  return next
}

export function getListinoPricingErrors(pricing: ListinoPricing): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!Number.isFinite(pricing.prezzo) || pricing.prezzo <= 0) {
    errors.prezzo = 'Prezzo deve essere maggiore di 0'
  }
  if (parseDiscountPercentage(pricing.sconto_percentuale) === null) {
    errors.sconto_percentuale = 'Inserisci uno sconto tra 0 e 100%, ad esempio 36,667'
  }
  const netPrice = parseNetPrice(pricing.prezzo_netto)
  if (netPrice === null) {
    errors.prezzo_netto = 'Inserisci il prezzo netto con al massimo due decimali, ad esempio 95,50'
  } else if (netPrice <= 0) {
    errors.prezzo_netto = 'Il prezzo netto deve essere maggiore di 0'
  }
  return errors
}
